import { useState, type FormEvent } from "react";
import { describeError } from "@/api/client";
import type { Uuid } from "@/api/types";
import {
  AlertDialog,
  Button,
  Dialog,
  DialogClose,
  InlineAlert,
  NumberField,
  RadioGroup,
  TextField,
  useToast,
} from "@/components";
import { Decimal, type DecimalString } from "@/lib/decimal";
import { formatGrams } from "@/lib/format";
import {
  useAdjustLot,
  useDeleteReorderPoint,
  useDisposeLot,
  useMoveLot,
  useSetReorderPoint,
  type Lot,
  type MaterialRef,
} from "../api";
import { LocationSelect } from "./LocationSelect";
import styles from "./LotDialogs.module.css";

type LotSummary = Pick<Lot, "id" | "code" | "display_code" | "material" | "balance_g" | "location">;

interface DialogBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MoveLotDialog({ lot, open, onOpenChange }: DialogBaseProps & { lot: LotSummary }) {
  const move = useMoveLot();
  const toast = useToast();
  const [locationId, setLocationId] = useState<Uuid | null>(null);
  const [error, setError] = useState<string | null>(null);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!locationId) {
      setError("Choose where the lot goes");
      return;
    }
    move.mutate(
      { lotId: lot.id, locationId },
      {
        onSuccess: (updated) => {
          toast.success(`Lot ${lot.display_code} moved to ${updated.location?.code ?? "its new place"}`);
          onOpenChange(false);
          setLocationId(null);
        },
        onError: (failure) => setError(describeError(failure)),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Move lot ${lot.display_code}`}
      description={`${lot.material.name}, now ${lot.location ? `at ${lot.location.code}` : "without a location"}.`}
      footer={
        <>
          <DialogClose />
          <Button variant="primary" onClick={() => submit()} pending={move.isPending}>
            Move lot
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <LocationSelect
          label="New location"
          value={locationId}
          onValueChange={(next) => {
            setLocationId(next);
            setError(null);
          }}
          allowNone={false}
          error={error}
        />
      </form>
    </Dialog>
  );
}

type AdjustMode = "count" | "delta";

export function AdjustLotDialog({ lot, open, onOpenChange }: DialogBaseProps & { lot: LotSummary }) {
  const adjust = useAdjustLot();
  const toast = useToast();
  const [mode, setMode] = useState<AdjustMode>("count");
  const [amount, setAmount] = useState<DecimalString | null>(null);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<{ amount?: string; reason?: string; form?: string }>({});

  const preview = (() => {
    const value = Decimal.tryFrom(amount);
    if (value === null) return null;
    const current = Decimal.from(lot.balance_g);
    return mode === "count" ? value.minus(current) : value;
  })();

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const next: typeof errors = {};
    if (amount === null)
      next.amount = mode === "count" ? "Enter the grams left in the container" : "Enter a change in grams";
    if (!reason.trim()) next.reason = "Say why, e.g. “spilled” or “counted”";
    if (preview !== null && preview.isZero()) next.amount = "That's the current balance; nothing would change";
    setErrors(next);
    if (Object.keys(next).length || amount === null) return;
    adjust.mutate(
      {
        lotId: lot.id,
        reason: reason.trim(),
        ...(mode === "count" ? { new_balance_g: amount } : { delta_g: amount }),
      },
      {
        onSuccess: (updated) => {
          toast.success(`Lot ${lot.display_code} now has ${formatGrams(updated.balance_g)} g`);
          onOpenChange(false);
          setAmount(null);
          setReason("");
        },
        onError: (failure) => setErrors({ form: describeError(failure) }),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Adjust stock of ${lot.display_code}`}
      description={`${lot.material.name}: the ledger says ${formatGrams(lot.balance_g)} g. To count by weighing, use Stocktake.`}
      footer={
        <>
          <DialogClose />
          <Button variant="primary" onClick={() => submit()} pending={adjust.isPending}>
            Save adjustment
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <RadioGroup<AdjustMode>
          label="How"
          orientation="horizontal"
          value={mode}
          onValueChange={(next) => {
            setMode(next);
            setAmount(null);
          }}
          options={[
            { value: "count", label: "Set what's left" },
            { value: "delta", label: "Add or remove" },
          ]}
        />
        <NumberField
          label={mode === "count" ? "Grams left" : "Change"}
          description={mode === "delta" ? "Negative to remove, e.g. −0.500" : undefined}
          value={amount}
          onValueChange={setAmount}
          unit="g"
          step={0.001}
          min={mode === "count" ? 0 : undefined}
          error={errors.amount}
        />
        {preview !== null && !preview.isZero() ? (
          <p className={styles.preview}>
            {preview.isNegative() ? "Removes" : "Adds"} {formatGrams(preview.abs().toString())} g
          </p>
        ) : null}
        <TextField label="Reason" value={reason} onValueChange={setReason} error={errors.reason} />
        {errors.form ? <InlineAlert tone="danger" title={errors.form} /> : null}
      </form>
    </Dialog>
  );
}

export function DisposeLotDialog({ lot, open, onOpenChange }: DialogBaseProps & { lot: LotSummary }) {
  const dispose = useDisposeLot();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Dispose of lot ${lot.display_code}?`}
      description={`The ${formatGrams(lot.balance_g)} g of ${lot.material.name} left are written off. The lot and its history stay on record.`}
      confirmLabel={`Dispose of lot ${lot.display_code}`}
      pending={dispose.isPending}
      onConfirm={() =>
        dispose.mutate(
          { lotId: lot.id, reason },
          {
            onSuccess: () => {
              toast.success(`Lot ${lot.display_code} disposed`);
              onOpenChange(false);
            },
            onError: (failure) => setError(describeError(failure)),
          },
        )
      }
    >
      <div className={styles.form}>
        <TextField label="Reason" optional value={reason} onValueChange={setReason} placeholder="Oxidised, spilled…" />
        {error ? <InlineAlert tone="danger" title={error} /> : null}
      </div>
    </AlertDialog>
  );
}

export function ReorderPointDialog({
  material,
  currentMin,
  open,
  onOpenChange,
}: DialogBaseProps & { material: MaterialRef; currentMin: DecimalString | null }) {
  const save = useSetReorderPoint();
  const remove = useDeleteReorderPoint();
  const toast = useToast();
  const [minG, setMinG] = useState<DecimalString | null>(currentMin);
  const [error, setError] = useState<string | null>(null);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const value = Decimal.tryFrom(minG);
    if (value === null || value.compare(0) <= 0) {
      setError("Enter more than 0 g");
      return;
    }
    save.mutate(
      { materialId: material.id, minG: value.toString() },
      {
        onSuccess: () => {
          toast.success(`Reorder point for ${material.name} saved`);
          onOpenChange(false);
        },
        onError: (failure) => setError(describeError(failure)),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Reorder point for ${material.name}`}
      description="When all your active lots of this material together hold less than this, it shows as low stock."
      size="sm"
      footer={
        <>
          {currentMin ? (
            <Button
              variant="ghost"
              pending={remove.isPending}
              onClick={() =>
                remove.mutate(material.id, {
                  onSuccess: () => {
                    toast.success(`Reorder point for ${material.name} removed`);
                    onOpenChange(false);
                  },
                  onError: (failure) => setError(describeError(failure)),
                })
              }
            >
              Remove
            </Button>
          ) : null}
          <DialogClose />
          <Button variant="primary" onClick={() => submit()} pending={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={styles.form}>
        <NumberField
          label="Reorder below"
          value={minG}
          onValueChange={setMinG}
          unit="g"
          min={0}
          step={0.001}
          error={error}
        />
      </form>
    </Dialog>
  );
}
